import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Navigation, Phone } from "lucide-react";
import StoreCard from "@/components/StoreCard";
import { KineticLines, Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { track } from "@/lib/track";

export default function StoreFinder({ limit }) {
  const [stores, setStores] = useState(null);
  const [error, setError] = useState(false);

  const load = () => {
    setError(false);
    setStores(null);
    api
      .get("/stores")
      .then((res) => setStores(limit ? res.data.slice(0, limit) : res.data))
      .catch(() => setError(true));
  };

  useEffect(load, [limit]);

  /* Home: feature the first RIFIC store + franchise bridge. */
  if (limit) {
    const store = stores?.[0];
    return (
      <section className="bg-white py-16 md:py-24" data-testid="store-finder-section">
        <div className="mx-auto max-w-[1440px] px-5 md:px-10">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">Our First Store</p>
          </Reveal>
          <KineticLines
            lines={["OUR FIRST RIFIC:"]}
            className="mt-3 block"
            lineClassName="font-display text-[clamp(2rem,4.2vw,3.25rem)] font-extrabold leading-[1.05] tracking-[-0.01em] text-deep"
          />

          {error && (
            <div className="mt-10 rounded-2xl border border-cream-line bg-cream p-8 text-center" data-testid="store-finder-error">
              <p className="text-base font-medium text-deep/70">Store details couldn't load right now.</p>
              <button
                onClick={load}
                data-testid="store-finder-retry-button"
                className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-deep px-6 text-xs font-bold uppercase tracking-[0.14em] text-cream"
              >
                Retry
              </button>
            </div>
          )}

          {!error && store === undefined && (
            <p className="mt-10 text-base text-deep/55" data-testid="store-finder-empty">
              Our first store details are coming online — check back soon.
            </p>
          )}

          {!error && store && (
            <Reveal delay={0.1}>
              <div data-testid="store-feature-karad" className="mt-8 rounded-[28px] border border-cream-line bg-cream p-6 md:mt-10 md:p-10">
                <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
                  <div className="lg:col-span-7">
                    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-orange-rific md:text-xs">Now Open</p>
                    <h3 className="mt-2 font-display text-4xl font-extrabold uppercase tracking-[-0.01em] text-deep md:text-6xl">
                      RIFIC
                      <br />
                      {store.city.toUpperCase()}
                    </h3>
                    <p className="mt-5 flex items-start gap-2 text-base leading-relaxed text-deep/75 md:text-lg">
                      <MapPin className="mt-1 h-4 w-4 shrink-0 text-orange-rific" />
                      <span>
                        {store.address.split(", ").map((line, i) => (
                          <span key={i} className="block">
                            {line}
                            {i < store.address.split(", ").length - 1 ? "," : ""}
                          </span>
                        ))}
                      </span>
                    </p>
                  </div>
                  <div className="flex flex-col gap-3 lg:col-span-5 lg:items-end">
                    <a
                      href={store.maps_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="store-feature-directions"
                      onClick={() => track("directions_click", { store: store.name, placement: "home_feature" })}
                      className="inline-flex h-14 items-center gap-2 rounded-full bg-orange-rific px-8 text-sm font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-deep"
                    >
                      <Navigation className="h-4 w-4" /> Get Directions
                    </a>
                    <a
                      href={`tel:${store.phone.replace(/\s/g, "")}`}
                      data-testid="store-feature-call"
                      onClick={() => track("phone_click", { store: store.name, placement: "home_feature" })}
                      className="inline-flex h-12 items-center gap-2 rounded-full border border-deep/20 px-7 text-xs font-bold uppercase tracking-[0.14em] text-deep transition-colors hover:border-orange-rific hover:text-orange-rific"
                    >
                      <Phone className="h-4 w-4" /> {store.phone}
                    </a>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          <Reveal delay={0.1}>
            <div className="mt-10 rounded-[28px] bg-deep p-8 text-center md:mt-14 md:p-14" data-testid="store-franchise-bridge">
              <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-orange-rific md:text-xs">Want RIFIC in your city?</p>
              <p className="mt-3 font-display text-3xl font-extrabold tracking-[-0.01em] text-cream md:text-5xl">
                BRING RIFIC TO YOUR CITY.
              </p>
              <Link
                to="/franchise"
                data-testid="store-bridge-franchise-cta"
                onClick={() => track("franchise_cta_click", { placement: "home_store_bridge" })}
                className="mt-7 inline-flex h-13 items-center gap-2 rounded-full bg-orange-rific px-8 py-4 text-sm font-bold uppercase tracking-[0.16em] text-white transition-colors duration-300 hover:bg-cream hover:text-deep"
              >
                Explore Franchise
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }

  /* Stores page: full list from the database. */
  return (
    <section className="bg-white py-16 md:py-24" data-testid="store-finder-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10">
        {error && (
          <div className="rounded-2xl border border-cream-line bg-cream p-8 text-center" data-testid="store-finder-error">
            <p className="text-base font-medium text-deep/70">Stores couldn't load right now.</p>
            <button
              onClick={load}
              data-testid="store-finder-retry-button"
              className="mt-4 inline-flex h-11 items-center gap-2 rounded-full bg-deep px-6 text-xs font-bold uppercase tracking-[0.14em] text-cream"
            >
              Retry
            </button>
          </div>
        )}

        {!error && stores === null && (
          <p className="text-base text-deep/50" data-testid="store-finder-loading">
            Finding RIFIC stores…
          </p>
        )}

        {!error && Array.isArray(stores) && stores.length === 0 && (
          <p className="rounded-2xl border border-cream-line bg-cream p-8 text-center text-base text-deep/70" data-testid="store-finder-empty">
            New stores are being added — check back soon.
          </p>
        )}

        {!error && Array.isArray(stores) && stores.length > 0 && (
          <div className="grid gap-5 md:grid-cols-2 md:gap-6" data-testid="store-finder-list">
            {stores.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                <StoreCard store={s} />
              </Reveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
